import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../apiHelpers';
import { formatEventDate } from '../utils/date-utils';
import AirspaceStatusPill from '../components/AirspaceStatusPill';
import { AirspaceStatus, AIRSPACE_STATUS_LABELS_SHORT } from '../utils/airspace-helpers';
import dayjs from 'dayjs';
import SearchField from '../components/ui/SearchField';
import { Chip, ChipGroup } from '../components/ui/Chip';
import Tooltip from '../components/ui/Tooltip';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

interface QueueItem {
  id: string;
  name: string;
  event_date: string;
  location: string | null;
  airspace_status: AirspaceStatus;
  airspace_invalidation_count: number;
  ref_counts: Partial<Record<string, number>>;
}

interface Props {
  statusFilter: string | null;
  onStatusFilterChange: (s: string | null) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Increment to force a re-fetch (parent bumps after a mutation). */
  version?: number;
}

const ALL_STATUSES: AirspaceStatus[] = ['needs_action', 'needs_review', 'needs_authorization', 'pending', 'complete'];

const AirspaceQueueList: React.FC<Props> = ({ statusFilter, onStatusFilterChange, selectedId, onSelect, version = 0 }) => {
  const api = useApi();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (showPast) params.set('show_past', '1');
    api.get(`/airspace/queue${params.toString() ? `?${params}` : ''}`, { silent: true })
      .then((d: QueueItem[]) => { if (!cancelled) { setQueue(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPast, version]);

  // Counts per status (server returned everything in scope; we compute counts after search-filter)
  const filtered = useMemo(() => {
    let result = queue;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        e => e.name.toLowerCase().includes(q) || (e.location || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      result = result.filter(e => e.airspace_status === statusFilter);
    }
    return result;
  }, [queue, search, statusFilter]);

  const counts: Record<string, number> = {};
  for (const s of ALL_STATUSES) counts[s] = queue.filter(q => q.airspace_status === s).length;

  return (
    <div className="flex flex-col h-full">
      {/* Filters — match EventList.js style (search input + Show Past toggle + chip filters) */}
      <div className="px-3 pt-3 pb-2 border-b border-gray-700/60 space-y-2">
        <div className="flex gap-2">
          <SearchField
            label="Search the queue"
            value={search}
            onChange={setSearch}
            placeholder="Search…"
            className="flex-1"
          />
          <Chip pressed={showPast} onClick={() => setShowPast(p => !p)} className="self-center">
            Show past
          </Chip>
        </div>
        <ChipGroup label="Filter by airspace status" className="!flex-nowrap overflow-x-auto pb-1 -mx-0.5 px-0.5 airspace-chip-row">
          <Chip pressed={!statusFilter} count={queue.length} onClick={() => onStatusFilterChange(null)}>
            All
          </Chip>
          {ALL_STATUSES.map(s => (
            <Tooltip key={s} label={AIRSPACE_STATUS_LABELS_SHORT[s]}>
              <Chip
                pressed={statusFilter === s}
                count={counts[s] || 0}
                onClick={() => onStatusFilterChange(statusFilter === s ? null : s)}
              >
                {AIRSPACE_STATUS_LABELS_SHORT[s]}
              </Chip>
            </Tooltip>
          ))}
        </ChipGroup>
        <style>{`
          .airspace-chip-row::-webkit-scrollbar { height: 4px; }
          .airspace-chip-row::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
          .airspace-chip-row::-webkit-scrollbar-track { background: transparent; }
        `}</style>
      </div>

      {/* Queue items */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <SkeletonGroup label="Loading the queue" className="space-y-px">
            {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14 w-full" />)}
          </SkeletonGroup>
        )}
        <ListStack
          items={loading ? null : filtered}
          getKey={q => q.id}
          label="Airspace queue"
          empty={search || statusFilter ? 'No events match this filter.' : 'No events in queue.'}
          renderRow={q => {
            const isPast = dayjs(q.event_date).isBefore(dayjs(), 'day');
            return (
              <ListRow selected={selectedId === q.id} onClick={() => onSelect(q.id)}>
                <span className="block">
                  <span className="flex items-start justify-between gap-2">
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm font-medium truncate ${isPast ? 'text-on-surface-variant' : 'text-on-surface'}`}>
                        {q.name}
                      </span>
                      <span className="block text-[11px] text-on-surface-variant mt-0.5">
                        {formatEventDate(q.event_date)}
                        {q.location ? ` · ${q.location.length > 28 ? q.location.slice(0, 28) + '…' : q.location}` : ''}
                      </span>
                    </span>
                    <AirspaceStatusPill status={q.airspace_status} size="table" />
                  </span>
                  {q.airspace_invalidation_count > 0 && (
                    <span className="mt-1 block text-[10px] text-amber-400/90">
                      ↻ {q.airspace_invalidation_count} prior attempt{q.airspace_invalidation_count > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </ListRow>
            );
          }}
        />
      </div>
    </div>
  );
};

export default AirspaceQueueList;
